"use client";
import type { NavigationModel } from "@lib/contentful/mappers/mapNavigation";
import { useRouteChange } from "@store/hooks/useRouteChange";
import { usePathname } from "next/navigation";
import { Flex } from "@ecco/ui/flex";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import type { FC, FocusEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NAV_HOVER_DELAY_MS } from "@components/layouts/navigation/styles";
import { AnimatedWrapper } from "./AnimatedWrapper";
import DesktopNavItem from "./DesktopNavItem";

export interface DesktopNavProps {
    navigation: NavigationModel;
    onCloseSearchBar?: () => void;
    onOpenSearchBar?: () => void;
    searchBarOpen?: boolean;
    setIsNavOpen: (value: boolean) => void;
}

export const DesktopNav: FC<DesktopNavProps> = ({ navigation, onCloseSearchBar, setIsNavOpen }) => {
    const pathname = usePathname();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [deviceSupportsHover, setDeviceSupportsHover] = useState<boolean>(false);
    const [heightNav, setHeightNav] = useState<number>(0);
    const [isChangingRoute, setIsChangingRoute] = useState(false);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const desktopNavRootRef = useRef<HTMLDivElement | null>(null);

    const closeMenu = useCallback(() => {
        onClose();
        setActiveItemId(null);
    }, [onClose]);

    useRouteChange(() => {
        onCloseSearchBar?.();
        closeMenu();
        if (closeTimeout.current) clearTimeout(closeTimeout.current);
        setIsNavOpen(false);
        setIsChangingRoute(true);
        setTimeout(() => setIsChangingRoute(false), 300);
    });

    useEffect(() => {
        const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
        setDeviceSupportsHover(!isTouchDevice);
    }, []);

    useEffect(() => {
        return () => {
            if (closeTimeout.current) clearTimeout(closeTimeout.current);
        };
    }, []);

    const clearOverlayTimeout = () => {
        if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };

    const scheduleOverlayClose = (afterClose?: () => void) => {
        clearOverlayTimeout();
        closeTimeout.current = setTimeout(() => {
            setIsNavOpen(false);
            afterClose?.();
        }, NAV_HOVER_DELAY_MS);
    };

    type OverlayVisibility = "show" | "hide" | "hold";

    const updateOverlay = useCallback(
        (visibility: OverlayVisibility) => {
            clearOverlayTimeout();

            if (visibility === "show") {
                setIsNavOpen(true);
            } else if (visibility === "hide") {
                setIsNavOpen(false);
            }
        },
        [setIsNavOpen]
    );

    const handleMouseEnter = () => {
        if (!deviceSupportsHover) return;
        updateOverlay("hold");
    };

    const handleMouseLeave = () => {
        if (!deviceSupportsHover) return;
        scheduleOverlayClose();
    };

    const handleDesktopNavFocus = useCallback(() => {
        updateOverlay("show");
    }, [updateOverlay]);

    const handleDesktopNavBlur = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget as Node | null;

            if (desktopNavRootRef.current?.contains(next)) return;

            scheduleOverlayClose(closeMenu);
        },
        [closeMenu]
    );

    return (
        <Flex
            ref={desktopNavRootRef}
            direction="row"
            align="stretch"
            justifyContent="center"
            h="100%"
            tabIndex={-1}
            data-testid="navPreSearchBox"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleDesktopNavFocus}
            onBlur={handleDesktopNavBlur}
        >
            {navigation.items.map((navigationItem) => {
                const itemId = navigationItem.id;

                return (
                    <DesktopNavItem
                        isOpen={isOpen}
                        // Each item owns its own open state, and closeMenu below cannot reach
                        // it - re-keying drops it on navigation, as remounting the nav used to.
                        key={`${itemId}-${pathname}`}
                        navigationItem={navigationItem}
                        setHeightNav={setHeightNav}
                        heightNav={heightNav}
                        onOpen={
                            deviceSupportsHover
                                ? () => {
                                      onOpen();
                                      setActiveItemId(itemId);
                                  }
                                : undefined
                        }
                        onClose={closeMenu}
                        isHoveringNavBar={deviceSupportsHover}
                        shouldRenderDropdown={Boolean(navigationItem.menu)}
                        onItemHover={() => updateOverlay("show")}
                        onItemEnter={() => updateOverlay("hold")}
                        onItemClose={() => updateOverlay("hide")}
                        isActive={activeItemId === itemId}
                    />
                );
            })}
            <AnimatedWrapper isChangingRoute={isChangingRoute} height={heightNav} />
        </Flex>
    );
};
